/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { DefaultButton, PrimaryButton } from '@fluentui/react/lib/Button';
import { Checkbox } from '@fluentui/react/lib/Checkbox';
import { Link } from '@fluentui/react/lib/Link';
import { Stack } from '@fluentui/react/lib/Stack';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisDebouncedSearchBox } from 'src/core/components/LuisDebouncedSearchBox';
import { LuisText } from 'src/core/components/LuisText';
import { SearchResultsAnnouncer } from 'src/core/components/SearchResultsAnnouncer';
import { StackBladeHost } from 'src/core/components/StackBladeHost';
import { TruncatedText } from 'src/core/components/TruncatedText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useUpdateState } from 'src/core/hooks/useUpdateState';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { substringSearchStrategy } from 'src/core/utils/searchStrategies/substringSearchStrategy';
import { TestBladeHeader } from 'src/luis/projects/testPane/components/common/TestBladeHeader';
import { QnaMakerFilterItem } from 'src/qnaMaker/appShell/qnaMakerEditPage/QnaMakerFiltersStore';

export type QnaMakerFilterBladeProps = {
    isOpen: boolean;
    questionsFilters: QnaMakerFilterItem[];
    sourcesFilters: QnaMakerFilterItem[];
    metadataFilters: QnaMakerFilterItem[];
    suggestionFilters: QnaMakerFilterItem[];
    showQuestionFiltersSection?: boolean;
    showSuggestionFiltersSection?: boolean;
    onFiltersChange: ({
        questionsFilters,
        sourcesFilters,
        metadataFilters,
        suggestionFilters
    }: {
        questionsFilters: QnaMakerFilterItem[];
        sourcesFilters: QnaMakerFilterItem[];
        metadataFilters: QnaMakerFilterItem[];
        suggestionFilters: QnaMakerFilterItem[];
    }) => void;
    onDismiss: () => void;
};

const messages = defineMessages({
    header: {
        id: 'qnaMaker.qnaMakerFiltersBlade.header',
        defaultMessage: 'Filters'
    },
    questionsInProgress: {
        id: 'qnaMaker.qnaMakerFiltersBlade.questionsInProgress',
        defaultMessage: 'Questions in progress'
    },
    unsavedQuestionPairsOnly: {
        id: 'qnaMaker.qnaMakerFiltersBlade.unsavedQuestionPairsOnly',
        defaultMessage: 'Unsaved questions pairs only'
    },
    suggestions: {
        id: 'qnaMaker.qnaMakerFiltersBlade.suggestions',
        defaultMessage: 'Suggestion type'
    },
    sources: {
        id: 'qnaMaker.qnaMakerFiltersBlade.sources',
        defaultMessage: 'Sources'
    },
    search: {
        id: 'qnaMaker.qnaMakerFiltersBlade.search',
        defaultMessage: 'Search'
    },
    noSourcesAvailable: {
        id: 'qnaMaker.qnaMakerFiltersBlade.noSourcesAvailable',
        defaultMessage: 'No sources available.'
    },
    noItemsFound: {
        id: 'qnaMaker.qnaMakerFiltersBlade.noItemsFound',
        defaultMessage: 'No items found.'
    },
    noMetadataAvailable: {
        id: 'qnaMaker.qnaMakerFiltersBlade.noMetadataAvailable',
        defaultMessage: 'No metadata available.'
    },
    metadata: {
        id: 'qnaMaker.qnaMakerFiltersBlade.metadata',
        defaultMessage: 'Metadata'
    },
    reset: {
        id: 'qnaMaker.qnaMakerFiltersBlade.reset',
        defaultMessage: 'Reset all filters'
    },
    apply: {
        id: 'qnaMaker.qnaMakerFiltersBlade.apply',
        defaultMessage: 'Apply'
    },
    cancel: {
        id: 'qnaMaker.qnaMakerFiltersBlade.cancel',
        defaultMessage: 'Cancel'
    }
});

const headerTheme = getScopedTheme('GlobalHeaderBlades');
const itemsLeftMargin = '6px';

/**
 * The filter blade parent component that render the filter blades
 */
export const QnaMakerFiltersBlade = observer((props: QnaMakerFilterBladeProps) => {
    const {
        isOpen,
        onDismiss,
        sourcesFilters,
        onFiltersChange,
        metadataFilters,
        questionsFilters,
        suggestionFilters,
        showQuestionFiltersSection = true,
        showSuggestionFiltersSection = false
    } = props;
    const { localize } = useLocalization();
    const [filterItems, updateFilterItems] = useUpdateState({ suggestionFilters, questionsFilters, sourcesFilters, metadataFilters });
    const [lastFocusedElement, setLastFocusedElement] = React.useState(null);

    React.useEffect(() => {
        document.getElementById(lastFocusedElement)?.focus();
    }, [lastFocusedElement]);

    const ItemsSection = ({
        items,
        title,
        toggleFilter,
        notAvailableMessage,
        isSearchHidden = false
    }: {
        title: string;
        isSearchHidden?: boolean;
        items: QnaMakerFilterItem[];
        notAvailableMessage?: string;
        toggleFilter: (item: QnaMakerFilterItem) => void;
    }) => {
        const [query, setQuery] = React.useState('');

        const filteredItems = substringSearchStrategy(items, query, ['name']);

        return items.length ? (
            <Stack tokens={{ childrenGap: 12, padding: '0 0 0 12px' }}>
                <LuisText variant="small" fontWeight={600} styles={{ root: { marginLeft: itemsLeftMargin } }}>
                    {title}
                </LuisText>
                {!isSearchHidden && (
                    <LuisDebouncedSearchBox
                        value={query}
                        onChange={(_, v) => setQuery(v)}
                        placeholder={localize(messages.search)}
                        debounceTime={0}
                    />
                )}

                <SearchResultsAnnouncer isActive={query.length > 0} itemsLength={filteredItems.length} />

                {filteredItems.length ? (
                    <Stack
                        key={title}
                        tokens={{ childrenGap: 6, maxHeight: window.innerHeight / 3 }}
                        styles={{ root: { overflow: 'auto', padding: '5px 0' } }}>
                        {filteredItems.map(item => (
                            <Checkbox
                                key={item.id}
                                label={item.name}
                                ariaLabel={item.name}
                                checked={item.isChecked}
                                id={`${item.id}-${item.isChecked}`}
                                styles={{ root: { marginLeft: itemsLeftMargin }, checkbox: { alignSelf: 'flex-start' } }}
                                onChange={() => {
                                    toggleFilter(item);
                                    setLastFocusedElement(`${item.id}-${!item.isChecked}`);
                                }}
                                onRenderLabel={() => (
                                    <TruncatedText
                                        title={item.name}
                                        maxWidth="300px"
                                        styles={{ root: { whiteSpace: 'pre-wrap !important' } }}>
                                        {item.name}
                                    </TruncatedText>
                                )}
                            />
                        ))}
                    </Stack>
                ) : (
                    <LuisText variant="small" styles={{ root: { margin: 'auto' } }}>
                        {localize(messages.noItemsFound)}
                    </LuisText>
                )}
            </Stack>
        ) : (
            <LuisText variant="small" styles={{ root: { margin: 'auto' } }}>
                {notAvailableMessage}
            </LuisText>
        );
    };

    const handleToggleFilterItem = (item: QnaMakerFilterItem) => {
        switch (item.filterType) {
            case 'suggestionsKind':
                updateFilterItems({
                    suggestionFilters: filterItems.suggestionFilters.map(i =>
                        i.id === item.id ? { ...i, isChecked: !i.isChecked } : { ...i, isChecked: false }
                    )
                });
                break;
            case 'questions':
                updateFilterItems({
                    questionsFilters: filterItems.questionsFilters.map(i => (i.id === item.id ? { ...i, isChecked: !i.isChecked } : i))
                });
                break;
            case 'metadata':
                updateFilterItems({
                    metadataFilters: filterItems.metadataFilters.map(i => (i.id === item.id ? { ...i, isChecked: !i.isChecked } : i))
                });
                break;
            case 'sources':
                updateFilterItems({
                    sourcesFilters: filterItems.sourcesFilters.map(i => (i.id === item.id ? { ...i, isChecked: !i.isChecked } : i))
                });
                break;
            default:
        }
    };

    const resetAllFilters = () => {
        updateFilterItems({
            suggestionFilters: filterItems.suggestionFilters.map(s => ({ ...s, isChecked: false })),
            questionsFilters: filterItems.questionsFilters.map(q => ({ ...q, isChecked: false })),
            sourcesFilters: filterItems.sourcesFilters.map(s => ({ ...s, isChecked: false })),
            metadataFilters: filterItems.metadataFilters.map(md => ({ ...md, isChecked: false }))
        });
    };

    const BladeContent = (
        <Stack verticalAlign="space-between" verticalFill>
            <Stack tokens={{ childrenGap: 16, padding: '12px 0 20px 0' }}>
                {showSuggestionFiltersSection && (
                    <ItemsSection
                        isSearchHidden
                        items={filterItems.suggestionFilters}
                        toggleFilter={handleToggleFilterItem}
                        title={localize(messages.suggestions)}
                    />
                )}
                {showQuestionFiltersSection && (
                    <ItemsSection
                        isSearchHidden
                        items={filterItems.questionsFilters}
                        toggleFilter={handleToggleFilterItem}
                        title={localize(messages.questionsInProgress)}
                    />
                )}
                <ItemsSection
                    items={filterItems.sourcesFilters}
                    title={localize(messages.sources)}
                    notAvailableMessage={localize(messages.noSourcesAvailable)}
                    toggleFilter={handleToggleFilterItem}
                />
                <ItemsSection
                    items={filterItems.metadataFilters}
                    title={localize(messages.metadata)}
                    notAvailableMessage={localize(messages.noMetadataAvailable)}
                    toggleFilter={handleToggleFilterItem}
                />
            </Stack>
            <Stack horizontal horizontalAlign="space-between">
                <Stack horizontal tokens={{ childrenGap: 6 }}>
                    <PrimaryButton
                        onClick={() => {
                            onFiltersChange(filterItems);
                            onDismiss();
                        }}>
                        {localize(messages.apply)}
                    </PrimaryButton>
                    <DefaultButton onClick={onDismiss}>{localize(messages.cancel)}</DefaultButton>
                </Stack>
                <Link onClick={resetAllFilters}>{localize(messages.reset)}</Link>
            </Stack>
        </Stack>
    );

    const blades = [
        {
            key: 'filtersBladeId',
            header: <TestBladeHeader>{localize(messages.header)}</TestBladeHeader>,
            content: BladeContent
        }
    ];

    return (
        <StackBladeHost
            isOpen={isOpen}
            blades={blades}
            onDismiss={onDismiss}
            onLastBladeDismissed={onDismiss}
            topSpacing={headerTheme.topSpacing}
        />
    );
});
